import { useMemo, useState, useEffect, useCallback } from "react";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { Grid, Typography, Button } from "@mui/material";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { getCrewGroups } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
// API Calls
import { useDispatch, useSelector } from "react-redux";
// Retrieve Crew info if not already loaded.
import { getCrew } from "src/redux/actions/CrewSchedule/getCrew";
import { gotCrewAction } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { ICrewBid } from "../helpers";
import { CrewBidTable, IExtendedCrewInPackage } from "./CrewBidTable";
import { SpinnerProgress } from "src/components/SpinnerProgress/SpinnerProgress";
import _ from "lodash";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import WarningIcon from "@mui/icons-material/Warning";

interface ICrewGroupsFieldProps {
  id: string;
  path: string[];
  isNew: boolean;
  crewBids: ICrewBid[];
  initialCrewGroups: string[];
  crewGroups: string[];
  populateCrew: boolean; // force a backend check/rebuild of crewBids
  setPopulateCrew: (state: boolean) => void;
  mosaicPane: HTMLElement | null;
}

export interface IGroupOption {
  value: string;
  key: string;
  label: string;
}

export interface ICrewInPackage extends ICrewBid {
  crewMember: ICrewMember | null;
}

/**
 * Crew Groups Field for Bid Package Editor
 * @param props -
 */
export const CrewGroupsField = (props: ICrewGroupsFieldProps): JSX.Element => {
  const { id, path, crewBids, initialCrewGroups, crewGroups, populateCrew, setPopulateCrew, mosaicPane } = props;
  const dispatch = useDispatch();

  // LOAD CREW MEMBERS
  const crew = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const [loadingCrew, setLoadingCrew] = useState<boolean>(false);
  const [cancelLoadingCrew, setCancelLoadingCrew] = useState<boolean>(false);

  const getCrewAction = useCallback(async () => {
    setLoadingCrew(true);
    const fetchedCrew: ICrewMember[] = await getCrew();
    if (!cancelLoadingCrew) {
      if (Array.isArray(fetchedCrew)) {
        dispatch(gotCrewAction(fetchedCrew));
      }
    }
    setLoadingCrew(false);
    return fetchedCrew;
  }, [dispatch, cancelLoadingCrew]);

  // Get initial crewmembers if not already available.
  useEffect(() => {
    if (!crew.length && !!getCrewAction) {
      getCrewAction();
    }
    return () => {
      setCancelLoadingCrew(true);
    };
  }, [getCrewAction, crew]);

  // LOAD CREW GROUPS
  const [crewGroupOptions, setCrewGroupOptions] = useState<string[]>([]);
  const getCrewGroupsAction = useCallback(() => {
    getCrewGroups()
      .then((data: string[]) => setCrewGroupOptions(data ?? []))
      .catch(() => setCrewGroupOptions([]));
  }, []);
  // Get initial crew groups if not already available.
  useEffect(() => {
    if (!crewGroupOptions?.length && !!getCrewGroupsAction) {
      getCrewGroupsAction();
    }
  }, [getCrewGroupsAction, crewGroupOptions]);

  const groupOptions: IGroupOption[] = useMemo(() => {
    if (!crewGroupOptions?.length) {
      return [];
    }
    const options = crewGroupOptions
      .map((group: string) => ({ value: group, key: group, label: group }))
      .sort((a, b) => {
        return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
      });
    return options;
  }, [crewGroupOptions]);

  // Check all crew in application that exist in groups selected and return sorted array of user ids.
  const crewInSelectedGroups = useMemo(() => {
    if (!crew?.length || !crewGroups?.length) {
      return [];
    }
    return crew
      ?.filter((crew) => {
        //const isActive = crew?.active && crew?.crewInfo?.isActive; // User and crew status are active.
        const existInGroup: boolean =
          crewGroups?.length === 0 ? false : crew.crewInfo?.groups?.some((g) => crewGroups.includes(g));
        return existInGroup; // && isActive;
      })
      .sort();
  }, [crew, crewGroups]);

  // If there are crew associated with this package, display these to the user.
  const crewInPackage: ICrewInPackage[] = useMemo(() => {
    //const crewList: ICrewInPackage[] = [];
    if (!crew?.length || !crewBids?.length) return [];
    return crewBids.map((cb) => {
      const crewMember = crew?.find((c) => c?._id === cb?.userId);
      return { ...cb, crewMember };
    });
  }, [crew, crewBids]);

  // Array of crew ids found in package
  const crewIdsInPackage = useMemo(() => crewInPackage?.map((c) => c?.userId).sort(), [crewInPackage]);
  // Array of crew ids found in selected groups.
  const crewIdsInSelectedGroups = useMemo(
    () => crewInSelectedGroups?.map((c) => c?._id).sort(),
    [crewInSelectedGroups]
  );

  // True if user has modified selected crew groups
  const isDirty = useMemo(() => {
    const dirty = !_.isEqual(crewGroups?.sort(), initialCrewGroups?.sort());
    setPopulateCrew(dirty);
    return dirty;
  }, [crewGroups, initialCrewGroups, setPopulateCrew]);

  const inactiveCrewInPackgeWarning: string = useMemo(() => {
    const warningPrefix = "Inactive crew in package will not receive bid pairings";
    const inactiveCrew = crewInPackage?.filter((c) => !c?.crewMember?.active);
    //let warning = "";
    //crewInPackage?.forEach((c, index) => {
    //  if (!c?.crewMember?.active || !c?.crewMember?.crewInfo?.isActive) {
    //    warning = `${warning} ${c?.crewMember?.firstName} ${c?.crewMember?.lastName}, `;
    //  }
    //});
    return inactiveCrew?.length ? `${warningPrefix}` : ""; //${warning}
  }, [crewInPackage]);

  // Check crew assigned to groups (in application) and compare to crew that are assigned to package.
  // If there is a mismatch between these 2 arrays of ids, warn the user that update package will potential modify crew bids.
  const crewInGroupsButNotPackage: string[] = useMemo(() => {
    return crewIdsInSelectedGroups.filter((x) => !crewIdsInPackage.includes(x));
  }, [crewIdsInPackage, crewIdsInSelectedGroups]);

  const crewInPackageButNotGroups: string[] = useMemo(() => {
    return crewIdsInPackage.filter((x) => !crewIdsInSelectedGroups.includes(x));
  }, [crewIdsInPackage, crewIdsInSelectedGroups]);

  const crewMismatchWarning: string = useMemo(() => {
    switch (true) {
      case isDirty:
        return "Crew Groups have been modified, rebuild of crew required.";
      case !isDirty && !!crewInGroupsButNotPackage?.length && !!crewInPackageButNotGroups?.length:
        return `${crewInPackageButNotGroups?.length} crew member(s) found in package that don't exist in selected groups. ${crewInGroupsButNotPackage?.length} crew member(s) found in selected groups that aren't included in current package. ${inactiveCrewInPackgeWarning}`;
      case !isDirty && !!crewInPackageButNotGroups?.length:
        return `${crewInPackageButNotGroups?.length} crew member(s) found in package that don't exist in selected groups. ${inactiveCrewInPackgeWarning}`;
      case !isDirty && !!crewInGroupsButNotPackage?.length:
        return `${crewInGroupsButNotPackage?.length} crew member(s) found in selected groups that aren't included in current package. ${inactiveCrewInPackgeWarning}`;
      case !isDirty && !!inactiveCrewInPackgeWarning?.length:
        return inactiveCrewInPackgeWarning;
      default:
        return ""; // If none of the scenarios above are true, return an empty string (no update needed)
    }
  }, [crewInGroupsButNotPackage, crewInPackageButNotGroups, isDirty, inactiveCrewInPackgeWarning]);

  // Create an array of crew objects to add to data (so users can see crew being added to existing dataset.)
  const crewBeingAddedToPackage = useMemo(() => {
    const crewAdded: IExtendedCrewInPackage[] = [];
    crewInGroupsButNotPackage?.forEach((crewId) => {
      const crewMember = crew?.find((c) => c?._id === crewId);
      //TODO Check with the team to make sure we wanna filter added crew so aggressively here.
      if (crewMember?._id) {
        //&& crewMember?.active && crewMember?.crewInfo?.isActive
        crewAdded.push({
          bidId: undefined,
          nfaBids: 0,
          pairingBids: 0,
          userId: crewMember._id,
          crewMember: crewMember,
          status: crewMember?.crewInfo?.isActive ? "added" : undefined,
        });
      }
    });
    return crewAdded;
  }, [crewInGroupsButNotPackage, crew]);

  return (
    <Grid container style={{ height: "100%", width: "100%" }}>
      <Typography className="required_fields_label">required fields</Typography>
      <Grid
        container
        direction="row"
        className="required_fields"
        alignItems="flex-start"
        alignContent="flex-start"
        style={{ width: "100%" }}
      >
        <Grid container>
          <FormikSelectField
            className="field selectfield crewgroups"
            label="Crew Groups"
            name="crewGroups"
            format={(crewGroups) =>
              crewGroups?.length ? crewGroups?.map((group: string) => ({ value: group, key: group, label: group })) : []
            }
            parse={(options: IGroupOption[]) => (options?.length ? options?.map((option) => option.key) : [])}
            options={groupOptions}
            isMulti={true}
            isClearable={true}
            menuPosition="fixed"
            maxMenuHeight={400}
            menuPortalTarget={mosaicPane}
            multiMode="checkbox"
            placeholder={Array.isArray(crewGroups) && crewGroups?.length ? crewGroups?.join(", ") : "Select..."}
          />
          <Button
            className="refresh_btn"
            variant="outlined"
            color="primary"
            onClick={_.debounce(() => {
              getCrewAction().then((response) => {
                if (Array.isArray(response)) {
                  // Only get crewGroups after crew have been retrieved.
                  getCrewGroupsAction();
                }
              });
            }, 500)}
          >
            Refresh Crew & Groups
          </Button>
        </Grid>
      </Grid>

      <Grid container style={{ marginBottom: -10, paddingLeft: 15 }} alignItems="center">
        {!!crewMismatchWarning?.length && (
          <>
            <WarningIcon color="secondary" fontSize="small" />
            <Typography variant="caption" color="secondary" style={{ margin: 5 }}>
              {crewMismatchWarning}
            </Typography>
          </>
        )}
        <CheckboxField
          className="force_crew_rebuild"
          label={`${isDirty ? "Required" : "Force"} Rebuild Of Crew`}
          value={populateCrew}
          onChange={() => {
            setPopulateCrew(!populateCrew);
          }}
          disabled={isDirty} // disable ability to set this false if crew groups have been modified.
          labelPlacement="end"
        />
      </Grid>

      <CrewBidTable
        crewInPackage={[...crewInPackage, ...crewBeingAddedToPackage]}
        path={path}
        id={id}
        crewAdded={crewBeingAddedToPackage.filter((c) => c.status === "added").map((c) => c.userId)}
        crewRemoved={crewInPackageButNotGroups}
      />
      <SpinnerProgress className="crewLoadingSpinner" loading={loadingCrew} message="loading crew info" />
    </Grid>
  );
};
